<template>
  <div class="pin-page py-3">
    <header class="pin-toolbar">
      <div class="pin-toolbar__title">
        <span class="pin-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Pincodes</h1>
          <p>Manage post-office and pincode records used for address lookups.</p>
        </div>
      </div>
      <div class="pin-toolbar__actions">
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadData"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          color="primary"
          size="sm"
          class="create-btn"
          @click="openCreate"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>New Pincode</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="pin-alert">{{ errorMessage }}</CAlert>

    <section class="pin-filters" aria-label="Filter pincodes">
      <div class="pin-filters__row">
        <div class="pin-search-wrap pin-filters__search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="pin-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="pin-input"
            placeholder="Search by pincode, office, district or state…"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="pin-search-clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>

        <div class="pin-filter">
          <label class="pin-filter__label">Office Type</label>
          <AppDropdown
            v-model="filters.OfficeType"
            :options="officeTypeFilterOptions"
            placeholder="All types"
          />
        </div>

        <div class="pin-filter">
          <label class="pin-filter__label">Delivery</label>
          <AppDropdown
            v-model="filters.Delivery"
            :options="deliveryFilterOptions"
            placeholder="All"
          />
        </div>

        <div class="pin-filter">
          <label class="pin-filter__label">State</label>
          <AppDropdown
            v-model="filters.StateName"
            :options="stateOptions"
            placeholder="All states"
            searchable
            search-placeholder="Search state…"
          />
        </div>

        <div class="pin-filter">
          <label class="pin-filter__label">District</label>
          <AppDropdown
            v-model="filters.District"
            :options="districtOptions"
            :placeholder="filters.StateName ? 'All districts' : 'Pick a state first'"
            :disabled="!filters.StateName || isLoadingDistricts"
            searchable
            search-placeholder="Search district…"
          />
        </div>

        <button
          v-if="hasActiveFilters"
          type="button"
          class="pin-filter__clear"
          @click="clearFilters"
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          <span>Clear filters</span>
        </button>
      </div>
    </section>

    <BaseTable
      :items="items"
      :columns="columns"
      title="Pincodes"
      accent="linear-gradient(180deg, #0e5b46, #16a07a)"
      :loading="isLoading"
      striped
      hover
      bordered
      sticky-header
      class="pin-table"
    >

      <template #cell-Pincode="{ row }">
        <span class="pin-code">{{ row.Pincode }}</span>
      </template>

      <template #cell-OfficeName="{ row }">
        <div class="pin-office-cell">
          <span class="pin-office-name">{{ row.OfficeName }}</span>
          <span v-if="row.DivisionName" class="pin-office-meta">{{ row.DivisionName }}</span>
        </div>
      </template>

      <template #cell-OfficeType="{ row }">
        <span class="pin-pill pin-pill--type">{{ row.OfficeType }}</span>
      </template>

      <template #cell-Delivery="{ row }">
        <span
          class="pin-pill"
          :class="row.Delivery === 'Delivery' ? 'pin-pill--green' : 'pin-pill--gray'"
        >{{ row.Delivery }}</span>
      </template>

      <template #cell-actions="{ row }">
        <div class="pin-row-actions">
          <button type="button" class="pin-action-btn pin-action-btn--edit" @click="openEdit(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            <span>Edit</span>
          </button>
          <button type="button" class="pin-action-btn pin-action-btn--delete" @click="openDelete(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </template>

      <template #empty>
        <div class="pin-empty">
          <div class="pin-empty__title">
            {{ searchTerm ? `No matches for "${searchTerm}"` : 'No pincodes yet' }}
          </div>
          <div class="pin-empty__hint">
            <button v-if="searchTerm" type="button" class="pin-empty__link" @click="searchTerm = ''">Clear search</button>
            <button v-else type="button" class="pin-empty__link" @click="openCreate">Add the first pincode</button>
          </div>
        </div>
      </template>
    </BaseTable>

    <footer v-if="items.length" class="pin-pagination">
      <div class="pin-pagination__meta">
        <span class="pin-pagination__info">
          Showing <strong>{{ rangeStart }}</strong>–<strong>{{ rangeEnd }}</strong>
          of <strong>{{ formattedTotal }}</strong>
        </span>
        <label class="pin-pagination__per-page">
          <span>Rows</span>
          <select v-model.number="perPage" class="pin-pagination__select" :disabled="isLoading">
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
        </label>
      </div>
      <AppPagination
        v-model="currentPage"
        :pages="lastPage"
        :disabled="isLoading"
        :max-visible="7"
        @change="loadData"
      />
    </footer>

    <transition name="pin-fade">
      <div
        v-if="formOpen"
        class="pin-modal-backdrop"
        @click.self="closeForm"
      >
        <div class="pin-modal" role="dialog" aria-modal="true" :aria-label="formTitle">
          <header class="pin-modal__header">
            <div class="pin-modal__title-block">
              <span class="pin-modal__accent" aria-hidden="true"></span>
              <div>
                <h2 class="pin-modal__title">{{ formTitle }}</h2>
                <p class="pin-modal__subtitle">{{ formSubtitle }}</p>
              </div>
            </div>
            <button
              type="button"
              class="pin-modal__close"
              aria-label="Close"
              @click="closeForm"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <form class="pin-form" @submit.prevent="saveForm">
            <div class="pin-form__grid">
              <div class="pin-form__row">
                <label for="pin-form-pincode" class="pin-form__label">
                  Pincode <span class="pin-form__required">*</span>
                </label>
                <input
                  id="pin-form-pincode"
                  ref="pincodeInput"
                  v-model.trim="form.Pincode"
                  type="text"
                  inputmode="numeric"
                  pattern="\d{6}"
                  maxlength="6"
                  class="pin-form__input"
                  placeholder="e.g. 670001"
                  required
                />
              </div>

              <div class="pin-form__row">
                <label for="pin-form-office" class="pin-form__label">
                  Office Name <span class="pin-form__required">*</span>
                </label>
                <input
                  id="pin-form-office"
                  v-model.trim="form.OfficeName"
                  type="text"
                  class="pin-form__input"
                  placeholder="e.g. Kannur H.O"
                  required
                />
              </div>

              <div class="pin-form__row">
                <label class="pin-form__label">
                  Office Type <span class="pin-form__required">*</span>
                </label>
                <div class="pin-form__segment" role="radiogroup" aria-label="Office Type">
                  <button
                    v-for="opt in officeTypeOptions"
                    :key="opt.value"
                    type="button"
                    role="radio"
                    :aria-checked="form.OfficeType === opt.value"
                    class="pin-form__segment-btn"
                    :class="{ 'pin-form__segment-btn--active': form.OfficeType === opt.value }"
                    @click="form.OfficeType = opt.value"
                  >{{ opt.label }}</button>
                </div>
              </div>

              <div class="pin-form__row">
                <label class="pin-form__label">
                  Delivery <span class="pin-form__required">*</span>
                </label>
                <div class="pin-form__segment" role="radiogroup" aria-label="Delivery">
                  <button
                    v-for="opt in deliveryOptions"
                    :key="opt.value"
                    type="button"
                    role="radio"
                    :aria-checked="form.Delivery === opt.value"
                    class="pin-form__segment-btn"
                    :class="{ 'pin-form__segment-btn--active': form.Delivery === opt.value }"
                    @click="form.Delivery = opt.value"
                  >{{ opt.label }}</button>
                </div>
              </div>

              <div class="pin-form__row">
                <label for="pin-form-district" class="pin-form__label">
                  District <span class="pin-form__required">*</span>
                </label>
                <input
                  id="pin-form-district"
                  v-model.trim="form.District"
                  type="text"
                  class="pin-form__input"
                  placeholder="e.g. Kannur"
                  required
                />
              </div>

              <div class="pin-form__row">
                <label for="pin-form-state" class="pin-form__label">
                  State <span class="pin-form__required">*</span>
                </label>
                <input
                  id="pin-form-state"
                  v-model.trim="form.StateName"
                  type="text"
                  class="pin-form__input"
                  placeholder="e.g. Kerala"
                  required
                />
              </div>

              <div class="pin-form__row pin-form__row--full">
                <p class="pin-form__hint">Latitude &amp; longitude are used to compute shipping/COD charges by distance. Leave blank only if unknown — shipping will fall back to ₹0 for this pincode.</p>
              </div>

              <div class="pin-form__row">
                <label for="pin-form-latitude" class="pin-form__label">Latitude</label>
                <input
                  id="pin-form-latitude"
                  v-model.trim="form.latitude"
                  type="number"
                  step="any"
                  min="-90"
                  max="90"
                  class="pin-form__input"
                  placeholder="e.g. 11.233665"
                />
              </div>

              <div class="pin-form__row">
                <label for="pin-form-longitude" class="pin-form__label">Longitude</label>
                <input
                  id="pin-form-longitude"
                  v-model.trim="form.longitude"
                  type="number"
                  step="any"
                  min="-180"
                  max="180"
                  class="pin-form__input"
                  placeholder="e.g. 76.052162"
                />
              </div>

              <div class="pin-form__row pin-form__row--full pin-form__geocode">
                <button
                  type="button"
                  class="pin-form__geocode-btn"
                  :disabled="!canGeocode || isGeocoding"
                  @click="autofetchCoords"
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="10" r="3"/>
                    <path d="M12 21s-7-7.58-7-12a7 7 0 0 1 14 0c0 4.42-7 12-7 12z"/>
                  </svg>
                  <span>{{ isGeocoding ? 'Looking up…' : 'Auto-fetch from address' }}</span>
                </button>
                <span
                  v-if="geocodeStatus"
                  class="pin-form__geocode-status"
                  :class="{ 'pin-form__geocode-status--error': geocodeError }"
                >{{ geocodeStatus }}</span>
              </div>

              <div class="pin-form__row">
                <label for="pin-form-circle" class="pin-form__label">Circle</label>
                <input
                  id="pin-form-circle"
                  v-model.trim="form.CircleName"
                  type="text"
                  class="pin-form__input"
                  placeholder="e.g. Kerala Circle"
                />
              </div>

              <div class="pin-form__row">
                <label for="pin-form-region" class="pin-form__label">Region</label>
                <input
                  id="pin-form-region"
                  v-model.trim="form.RegionName"
                  type="text"
                  class="pin-form__input"
                  placeholder="e.g. Kozhikode Region"
                />
              </div>

              <div class="pin-form__row pin-form__row--full">
                <label for="pin-form-division" class="pin-form__label">Division</label>
                <input
                  id="pin-form-division"
                  v-model.trim="form.DivisionName"
                  type="text"
                  class="pin-form__input"
                  placeholder="e.g. Kannur Division"
                />
              </div>
            </div>

            <CAlert v-if="formError" color="danger" show class="pin-form__alert">{{ formError }}</CAlert>

            <footer class="pin-form__footer">
              <button type="button" class="pin-btn pin-btn--ghost" :disabled="isSaving" @click="closeForm">Cancel</button>
              <button type="submit" class="pin-btn pin-btn--primary" :disabled="!canSubmit || isSaving">
                {{ isSaving ? 'Saving…' : (isEditing ? 'Save changes' : 'Create pincode') }}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="pin-fade">
      <div
        v-if="deleteTarget"
        class="pin-modal-backdrop"
        @click.self="closeDelete"
      >
        <div class="pin-modal pin-modal--confirm" role="alertdialog" aria-modal="true" aria-label="Confirm delete">
          <header class="pin-modal__header">
            <div class="pin-modal__title-block">
              <span class="pin-modal__accent pin-modal__accent--danger" aria-hidden="true"></span>
              <div>
                <h2 class="pin-modal__title">Delete pincode?</h2>
                <p class="pin-modal__subtitle">This cannot be undone.</p>
              </div>
            </div>
            <button
              type="button"
              class="pin-modal__close"
              aria-label="Close"
              @click="closeDelete"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <div class="pin-confirm-body">
            <p class="pin-confirm-body__lead">
              "<strong>{{ deleteTarget.OfficeName }}</strong>" ({{ deleteTarget.Pincode }}) will be removed permanently.
            </p>
          </div>

          <footer class="pin-form__footer">
            <button type="button" class="pin-btn pin-btn--ghost" :disabled="isDeleting" @click="closeDelete">Cancel</button>
            <button type="button" class="pin-btn pin-btn--danger" :disabled="isDeleting" @click="confirmDelete">
              {{ isDeleting ? 'Deleting…' : 'Delete pincode' }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import AppPagination from '@/components/AppPagination.vue'

const EMPTY_FILTERS = () => ({
  OfficeType: '',
  Delivery: '',
  StateName: '',
  District: '',
})

const EMPTY_FORM = () => ({
  id: null,
  Pincode: '',
  OfficeName: '',
  OfficeType: 'BO',
  Delivery: 'Delivery',
  District: '',
  StateName: '',
  latitude: '',
  longitude: '',
  CircleName: '',
  RegionName: '',
  DivisionName: '',
})

const SEARCH_DEBOUNCE_MS = 350

export default {
  name: 'SettingsPincodes',
  components: { BaseTable, AppDropdown, AppPagination },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      items: [],
      searchTerm: '',
      searchTimer: null,
      currentPage: 1,
      perPage: 25,
      lastPage: 1,
      total: 0,
      filters: EMPTY_FILTERS(),
      states: [],
      districts: [],
      isLoadingDistricts: false,
      columns: [
        { key: 'Pincode', label: 'Pincode', width: '110px' },
        { key: 'OfficeName', label: 'Office' },
        { key: 'OfficeType', label: 'Type', width: '80px', sortable: false },
        { key: 'Delivery', label: 'Delivery', width: '110px', sortable: false },
        { key: 'District', label: 'District', width: '150px' },
        { key: 'StateName', label: 'State', width: '140px' },
        { key: 'actions', label: '', width: '170px', align: 'right', sortable: false },
      ],
      officeTypeOptions: [
        { value: 'HO', label: 'HO' },
        { value: 'SO', label: 'SO' },
        { value: 'BO', label: 'BO' },
        { value: 'PO', label: 'PO' },
      ],
      deliveryOptions: [
        { value: 'Delivery', label: 'Delivery' },
        { value: 'Non-Delivery', label: 'Non-Delivery' },
      ],
      formOpen: false,
      form: EMPTY_FORM(),
      formError: '',
      isSaving: false,
      isGeocoding: false,
      geocodeStatus: '',
      geocodeError: false,
      deleteTarget: null,
      isDeleting: false,
    }
  },
  computed: {
    isEditing () {
      return this.form.id !== null && this.form.id !== undefined
    },
    formTitle () {
      return this.isEditing ? 'Edit pincode' : 'New pincode'
    },
    formSubtitle () {
      return this.isEditing
        ? 'Update the details of this pincode record.'
        : 'Add a new pincode / post-office entry.'
    },
    canSubmit () {
      return (
        /^\d{6}$/.test(String(this.form.Pincode).trim()) &&
        this.form.OfficeName.trim().length > 0 &&
        this.form.OfficeType &&
        this.form.Delivery &&
        this.form.District.trim().length > 0 &&
        this.form.StateName.trim().length > 0
      )
    },
    canGeocode () {
      return /^\d{6}$/.test(String(this.form.Pincode).trim())
    },
    rangeStart () {
      if (!this.total) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    rangeEnd () {
      if (!this.total) return 0
      return Math.min(this.currentPage * this.perPage, this.total)
    },
    formattedTotal () {
      try {
        return new Intl.NumberFormat('en-IN').format(this.total || 0)
      } catch (e) {
        return String(this.total || 0)
      }
    },
    officeTypeFilterOptions () {
      return [{ value: '', label: 'All types' }, ...this.officeTypeOptions]
    },
    deliveryFilterOptions () {
      return [{ value: '', label: 'All' }, ...this.deliveryOptions]
    },
    stateOptions () {
      const opts = (this.states || []).map((name) => ({ value: name, label: name }))
      return [{ value: '', label: 'All states' }, ...opts]
    },
    districtOptions () {
      const opts = (this.districts || []).map((name) => ({ value: name, label: name }))
      return [{ value: '', label: 'All districts' }, ...opts]
    },
    hasActiveFilters () {
      return !!(this.filters.OfficeType || this.filters.Delivery || this.filters.StateName || this.filters.District)
    },
  },
  watch: {
    searchTerm () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.loadData()
      }, SEARCH_DEBOUNCE_MS)
    },
    perPage () {
      this.currentPage = 1
      this.loadData()
    },
    'filters.OfficeType' () {
      this.currentPage = 1
      this.loadData()
    },
    'filters.Delivery' () {
      this.currentPage = 1
      this.loadData()
    },
    'filters.StateName' (now, prev) {
      // Reset dependent district filter when state changes.
      if (now !== prev) this.filters.District = ''
      this.districts = []
      if (now) this.loadDistricts(now)
      this.currentPage = 1
      this.loadData()
    },
    'filters.District' () {
      this.currentPage = 1
      this.loadData()
    },
  },
  created () {
    this.loadData()
    this.loadStates()
  },
  beforeDestroy () {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    loadData () {
      this.isLoading = true
      this.errorMessage = ''

      const params = {
        page: this.currentPage,
        per_page: this.perPage,
      }
      if (this.searchTerm) params.search = this.searchTerm
      if (this.filters.OfficeType) params.OfficeType = this.filters.OfficeType
      if (this.filters.Delivery) params.Delivery = this.filters.Delivery
      if (this.filters.StateName) params.StateName = this.filters.StateName
      if (this.filters.District) params.District = this.filters.District

      axios.get(`${this.$apiAdress}/api/admin-pincodes`, { params })
        .then((response) => {
          const payload = response.data || {}
          this.items = Array.isArray(payload.data) ? payload.data : []
          this.currentPage = payload.current_page || 1
          this.lastPage = payload.last_page || 1
          this.total = payload.total || 0
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load pincodes right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    loadStates () {
      axios.get(`${this.$apiAdress}/api/address/states`)
        .then((response) => {
          const data = response.data
          const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
          this.states = list
            .map((s) => (typeof s === 'string' ? s : (s?.state || s?.StateName)))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
        })
        .catch(() => {
          this.states = []
        })
    },
    loadDistricts (state) {
      if (!state) {
        this.districts = []
        return
      }
      this.isLoadingDistricts = true
      axios.get(`${this.$apiAdress}/api/address/districts/${encodeURIComponent(state)}`)
        .then((response) => {
          const data = response.data
          const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
          this.districts = list
            .map((d) => (typeof d === 'string' ? d : (d?.District || d?.district)))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
        })
        .catch(() => {
          this.districts = []
        })
        .finally(() => {
          this.isLoadingDistricts = false
        })
    },
    clearFilters () {
      this.filters = EMPTY_FILTERS()
      this.districts = []
    },
    openCreate () {
      this.form = EMPTY_FORM()
      this.formError = ''
      this.resetGeocodeStatus()
      this.formOpen = true
      this.$nextTick(() => {
        if (this.$refs.pincodeInput) this.$refs.pincodeInput.focus()
      })
    },
    openEdit (row) {
      this.form = {
        id: row.id,
        Pincode: row.Pincode !== null && row.Pincode !== undefined ? String(row.Pincode) : '',
        OfficeName: row.OfficeName || '',
        OfficeType: row.OfficeType || 'BO',
        Delivery: row.Delivery || 'Delivery',
        District: row.District || '',
        StateName: row.StateName || '',
        latitude: row.latitude !== null && row.latitude !== undefined ? String(row.latitude) : '',
        longitude: row.longitude !== null && row.longitude !== undefined ? String(row.longitude) : '',
        CircleName: row.CircleName || '',
        RegionName: row.RegionName || '',
        DivisionName: row.DivisionName || '',
      }
      this.formError = ''
      this.resetGeocodeStatus()
      this.formOpen = true
      this.$nextTick(() => {
        if (this.$refs.pincodeInput) this.$refs.pincodeInput.focus()
      })
    },
    closeForm () {
      if (this.isSaving) return
      this.formOpen = false
      this.formError = ''
      this.resetGeocodeStatus()
    },
    resetGeocodeStatus () {
      this.geocodeStatus = ''
      this.geocodeError = false
    },
    async autofetchCoords () {
      if (!this.canGeocode || this.isGeocoding) return

      const parts = [
        this.form.OfficeName,
        this.form.District,
        this.form.StateName,
        String(this.form.Pincode).trim(),
        'India',
      ].map((s) => (s ? String(s).trim() : '')).filter(Boolean)

      const query = parts.join(', ')
      this.isGeocoding = true
      this.geocodeStatus = ''
      this.geocodeError = false

      try {
        const response = await axios.get(`${this.$apiAdress}/api/admin-pincodes/geocode`, {
          params: { q: query },
        })
        const lat = response?.data?.latitude
        const lng = response?.data?.longitude
        if (lat == null || lng == null) {
          this.geocodeError = true
          this.geocodeStatus = 'No match — enter manually.'
          return
        }
        this.form.latitude = String(lat)
        this.form.longitude = String(lng)
        const label = response?.data?.display_name
        this.geocodeStatus = label ? `Found: ${label}` : 'Coordinates filled in.'
      } catch (error) {
        this.geocodeError = true
        if (error?.response?.status === 404) {
          this.geocodeStatus = 'No match — enter manually.'
        } else {
          this.geocodeStatus = error?.response?.data?.error || 'Lookup failed. Try again.'
        }
      } finally {
        this.isGeocoding = false
      }
    },
    async saveForm () {
      if (!this.canSubmit || this.isSaving) return
      this.isSaving = true
      this.formError = ''

      const latitudeRaw = String(this.form.latitude ?? '').trim()
      const longitudeRaw = String(this.form.longitude ?? '').trim()

      const payload = {
        Pincode: Number(this.form.Pincode),
        OfficeName: this.form.OfficeName.trim(),
        OfficeType: this.form.OfficeType,
        Delivery: this.form.Delivery,
        District: this.form.District.trim(),
        StateName: this.form.StateName.trim(),
        latitude: latitudeRaw === '' ? null : Number(latitudeRaw),
        longitude: longitudeRaw === '' ? null : Number(longitudeRaw),
        CircleName: this.form.CircleName ? this.form.CircleName.trim() : '',
        RegionName: this.form.RegionName ? this.form.RegionName.trim() : '',
        DivisionName: this.form.DivisionName ? this.form.DivisionName.trim() : '',
      }

      try {
        if (this.isEditing) {
          await axios.put(`${this.$apiAdress}/api/admin-pincodes/${this.form.id}`, payload)
          this.$toast.success('Pincode updated')
        } else {
          await axios.post(`${this.$apiAdress}/api/admin-pincodes`, payload)
          this.$toast.success('Pincode created')
        }
        this.formOpen = false
        await this.loadData()
      } catch (error) {
        const message = error?.response?.data?.error
        if (message && typeof message === 'object') {
          this.formError = Object.values(message).flat().join(' ')
        } else if (typeof message === 'string') {
          this.formError = message
        } else {
          this.formError = 'Save failed. Please try again.'
        }
      } finally {
        this.isSaving = false
      }
    },
    openDelete (row) {
      this.deleteTarget = row
    },
    closeDelete () {
      if (this.isDeleting) return
      this.deleteTarget = null
    },
    async confirmDelete () {
      if (!this.deleteTarget || this.isDeleting) return
      this.isDeleting = true
      try {
        await axios.delete(`${this.$apiAdress}/api/admin-pincodes/${this.deleteTarget.id}`)
        this.$toast.success('Pincode deleted')
        this.deleteTarget = null
        await this.loadData()
      } catch (error) {
        const message = error?.response?.data?.error || 'Delete failed. Please try again.'
        this.$toast.error(typeof message === 'string' ? message : 'Delete failed.')
      } finally {
        this.isDeleting = false
      }
    },
  },
}
</script>

<style scoped>
.pin-page {
  padding: 0.25rem 0;
}

.pin-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.pin-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1 1 auto;
  min-width: 0;
}

.pin-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.pin-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.pin-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.pin-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.pin-toolbar__actions >>> .btn.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.pin-toolbar__actions >>> .btn.create-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.pin-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.pin-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.refresh-btn__icon {
  flex-shrink: 0;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.pin-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.pin-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: pin-refresh-spin 0.9s linear infinite;
}

@keyframes pin-refresh-spin {
  to { transform: rotate(360deg); }
}

.create-btn__icon {
  flex-shrink: 0;
}

.pin-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.pin-filters {
  margin: 0 0 0.85rem;
  padding: 0.75rem 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.pin-filters__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.65rem 0.75rem;
}

.pin-filters__search {
  width: 280px;
  align-self: flex-end;
  margin-bottom: 0;
}

.pin-filter {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 150px;
  flex: 1 1 150px;
  max-width: 220px;
}

.pin-filter__label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.pin-filter__clear {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 38px;
  padding: 0 0.85rem;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.pin-filter__clear:hover {
  border-color: #ef4444;
  background: #fee2e2;
  color: #991b1b;
}

.pin-table {
  margin-bottom: 0.75rem;
}

.pin-search-wrap {
  position: relative;
  width: 320px;
  max-width: 100%;
}

.pin-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.pin-input {
  width: 100%;
  height: 34px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pin-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.pin-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pin-code {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.86rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.04em;
}

.pin-office-cell {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.pin-office-name {
  font-size: 0.86rem;
  font-weight: 600;
  color: #1f2a44;
}

.pin-office-meta {
  font-size: 0.72rem;
  color: #6b7280;
}

.pin-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.pin-pill--type {
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
}

.pin-pill--green {
  background: #ecfdf5;
  color: #0e5b46;
  border: 1px solid #c7ebda;
}

.pin-pill--gray {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.pin-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: flex-end;
}

.pin-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.pin-action-btn--edit:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.pin-action-btn--delete:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.pin-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}

.pin-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.pin-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.pin-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.pin-empty__link:hover {
  text-decoration: underline;
}

.pin-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  padding: 0.7rem 0.95rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  font-size: 0.8rem;
  color: #4b5563;
}

.pin-pagination__meta {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.pin-pagination__info {
  font-size: 0.78rem;
  color: #6b7280;
}

.pin-pagination__info strong {
  color: #1f2a44;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pin-pagination__per-page {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  color: #6b7280;
  font-weight: 600;
}

.pin-pagination__select {
  height: 30px;
  padding: 0 0.5rem;
  border: 1px solid #d8dde6;
  border-radius: 6px;
  background: #ffffff;
  font-size: 0.78rem;
  color: #1f2a44;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pin-pagination__select:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.pin-pagination__select:disabled {
  background: #f8fafc;
  color: #9ca3af;
  cursor: not-allowed;
}

.pin-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.pin-modal {
  width: 640px;
  max-width: 100%;
  max-height: calc(100vh - 2.5rem);
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pin-modal--confirm {
  width: 460px;
}

.pin-modal__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.1rem 0.9rem;
  border-bottom: 1px solid #e5e9f2;
  background: #fbfcfe;
}

.pin-modal__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.pin-modal--confirm .pin-modal__header::before {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
}

.pin-modal__title-block {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  min-width: 0;
}

.pin-modal__accent {
  margin-top: 4px;
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  flex: 0 0 auto;
}

.pin-modal__accent--danger {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
}

.pin-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
}

.pin-modal__subtitle {
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.pin-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
}

.pin-modal__close:hover {
  background: #f3f6fb;
  color: #1f2a44;
}

.pin-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.1rem 1.1rem 0;
  overflow-y: auto;
}

.pin-form__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem 1rem;
}

.pin-form__row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.pin-form__row--full {
  grid-column: 1 / -1;
}

.pin-form__label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
}

.pin-form__required {
  color: #ef4444;
  margin-left: 2px;
}

.pin-form__hint {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: #f5fbf8;
  border: 1px solid #c7ebda;
  border-radius: 8px;
  font-size: 0.74rem;
  color: #0e5b46;
  line-height: 1.45;
}

.pin-form__geocode {
  flex-direction: row;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.pin-form__geocode-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.85rem;
  border-radius: 8px;
  border: 1px solid #c7ebda;
  background: #f5fbf8;
  color: #0e5b46;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.pin-form__geocode-btn:hover:not(:disabled) {
  border-color: #16a07a;
  background: #ecfdf5;
}

.pin-form__geocode-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.pin-form__geocode-status {
  font-size: 0.74rem;
  color: #4b5563;
  line-height: 1.4;
  flex: 1 1 60%;
  min-width: 0;
}

.pin-form__geocode-status--error {
  color: #b91c1c;
}

.pin-form__input {
  width: 100%;
  height: 38px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.86rem;
  color: #1f2a44;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pin-form__input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.pin-form__segment {
  display: inline-flex;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  padding: 3px;
  background: #f7f9fc;
  width: fit-content;
}

.pin-form__segment-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.pin-form__segment-btn--active {
  background: #ffffff;
  color: #0e5b46;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.pin-form__alert {
  margin: 0;
  border-radius: 8px;
  font-size: 0.78rem;
}

.pin-form__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.9rem 1.1rem 1.1rem;
  margin-top: 0.4rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
}

.pin-confirm-body {
  padding: 1rem 1.1rem 0;
}

.pin-confirm-body__lead {
  margin: 0;
  font-size: 0.86rem;
  color: #1f2a44;
  line-height: 1.5;
}

.pin-confirm-body__lead strong {
  color: #0e5b46;
}

.pin-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease, transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease;
}

.pin-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.pin-btn--ghost {
  background: #ffffff;
  border-color: #d8dde6;
  color: #1f2a44;
}

.pin-btn--ghost:hover:not(:disabled) {
  background: #f3f6fb;
  border-color: #c7cfdb;
}

.pin-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.pin-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.pin-btn--danger {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(185, 28, 28, 0.2);
}

.pin-btn--danger:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.pin-fade-enter-active,
.pin-fade-leave-active {
  transition: opacity 0.18s ease;
}

.pin-fade-enter,
.pin-fade-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .pin-toolbar {
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .pin-toolbar__title {
    flex: 1 1 100%;
  }
  .pin-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start;
  }
  .pin-search-wrap {
    width: 100%;
  }
  .pin-filters__search,
  .pin-filter {
    width: 100%;
    max-width: none;
    flex: 1 1 100%;
  }
  .pin-modal {
    width: 100%;
  }
  .pin-form__grid {
    grid-template-columns: 1fr;
  }
  .pin-pagination {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (max-width: 575px) {
  .pin-page {
    padding: 0 0 0.5rem;
  }
  .pin-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .pin-toolbar__accent {
    height: 30px;
  }
  .pin-toolbar__title h1 {
    font-size: 1rem;
  }
  .pin-toolbar__title p {
    font-size: 0.72rem;
  }
  .pin-toolbar__actions {
    gap: 0.4rem;
  }
  .pin-toolbar__actions >>> .btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .pin-modal-backdrop {
    padding: 0.5rem;
    align-items: flex-end;
  }
  .pin-modal {
    max-height: calc(100vh - 1rem);
    border-radius: 12px;
  }
  .pin-modal__header {
    padding: 0.85rem 0.85rem 0.75rem;
  }
  .pin-row-actions {
    flex-wrap: wrap;
    gap: 0.3rem;
  }
}
</style>
